import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Navbar } from '../components/Navbar';

export const StoreAccess = () => {
  const [storeId, setStoreId] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleAccess = async () => {
    if (!storeId) {
      toast.error('Digite o ID da loja');
      return;
    }

    // Validar formato (14-16 dígitos)
    if (!/^\d{14,16}$/.test(storeId)) {
      toast.error('ID inválido. Use 14 a 16 dígitos numéricos');
      return;
    }

    setLoading(true);

    try {
      // Verificar se a loja existe
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/store/${storeId}`
      );

      if (response.ok) {
        navigate(`/loja/${storeId}`);
      } else {
        toast.error('Loja não encontrada. Verifique o ID');
      }
    } catch (error) {
      toast.error('Erro ao acessar loja');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAccess();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl mb-6">
            <Store className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="store-access-title">
            Acessar Loja
          </h1>
          <p className="text-lg text-gray-600">
            Digite o ID da loja que você deseja visitar
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                ID da Loja
              </label>
              <Input
                type="text"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value.replace(/\D/g, '').slice(0, 16))}
                onKeyPress={handleKeyPress}
                placeholder="Digite o ID (14 a 16 dígitos)"
                className="text-xl text-center tracking-wider"
                maxLength={16}
                data-testid="store-id-input"
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                {storeId.length > 0 && `${storeId.length} de 14-16 dígitos`}
              </p>
            </div>

            <Button
              onClick={handleAccess}
              disabled={loading || storeId.length < 14}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 h-14 text-lg"
              data-testid="access-store-btn"
            >
              {loading ? (
                'Verificando...'
              ) : (
                <>
                  Acessar Loja
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="bg-indigo-50 rounded-lg p-4">
              <h3 className="font-semibold text-indigo-900 mb-2">
                💡 Como funciona?
              </h3>
              <ul className="text-sm text-indigo-800 space-y-2">
                <li>• Cada loja possui um ID único de 14 a 16 dígitos</li>
                <li>• Peça o ID para o vendedor</li>
                <li>• Digite aqui e acesse a vitrine completa</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
